import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { FaSearch, FaUserCog, FaBuilding } from 'react-icons/fa'
import { FiRefreshCw, FiX } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useDebounce } from '../../../hooks/useDebounce'

interface TeamMember {
  id: number
  first_name: string
  last_name: string
  username: string
  email: string
  role_id: number
  vendor_id: number
  status: string
  role?: { id: number; name: string }
  vendor?: { id: number; vendor_full_name: string }
}

const STATUS_COLORS: Record<string, string> = {
  VERIFIED: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  SUSPENDED: 'bg-red-100 text-red-700',
  DEACTIVATED: 'bg-gray-100 text-gray-500',
}

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: '7', label: 'Site Engineer' },
  { value: '9', label: 'Report Maker' },
]

export default function VendorPortalTeam() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  const debouncedSearch = useDebounce(searchInput, 400)

  const fetchTeam = useCallback(() => {
    setLoading(true)
    const params: any = {}
    if (roleFilter) params.role_id = roleFilter
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/team', { params })
      .then(r => setMembers(r.data.data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [roleFilter, debouncedSearch])

  useEffect(() => { fetchTeam() }, [fetchTeam])

  const clearSearch = () => setSearchInput('')

  return (
    <>
      <Helmet><title>Vendor Portal – Team</title></Helmet>
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Team</h2>
              <p className="text-sm text-gray-500 mt-0.5">{members.length} team member{members.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={fetchTeam}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium shadow-sm"
            >
              <FiRefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50/50 flex flex-wrap gap-3 items-center">
          <div className="relative flex items-center">
            <FaSearch className="absolute left-3 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search name / username / email..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-72"
            />
            {searchInput && (
              <button onClick={clearSearch} className="absolute right-2 text-gray-400 hover:text-gray-600">
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {ROLE_OPTIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-10">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <FaUserCog className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No team members found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              ) : members.map((m, i) => (
                <tr key={m.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-4 text-gray-400 text-xs">{i + 1}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-700 text-xs font-semibold">
                          {m.first_name?.[0]}{m.last_name?.[0]}
                        </span>
                      </div>
                      <span className="font-medium text-gray-900">{m.first_name} {m.last_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {m.vendor ? (
                      <div className="flex items-center gap-1.5">
                        <FaBuilding className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700">{m.vendor.vendor_full_name}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-gray-600">{m.username}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-gray-600">{m.email}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <FaUserCog className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{m.role?.name ?? `Role #${m.role_id}`}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[m.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {m.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Info footer */}
        <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50/50">
          <p className="text-xs text-gray-500">You can reassign site engineers and report makers from the individual site view page.</p>
        </div>
      </div>
    </>
  )
}
